import { useState, type ReactNode } from "react";
import { API_BASE } from "../../lib/api.ts";
import type { ComplianceFramework, ComplianceWindow } from "./use-compliance.ts";

/**
 * The CSV/JSON export affordance (task 10.5; design C6; Requirements 4.5, 5.1). It downloads the
 * tenant's mapped findings for the CURRENT framework + window from
 * `GET /api/v1/compliance/export?framework=…&from=…&to=…&format=csv|json`, the viewer-gated export
 * read (`exportComplianceMapping`).
 *
 * The export MUST travel the SAME authenticated path as the view: the SPA calls the BFF at the same
 * origin under `/api/v1`, and the BFF proxies to `core` with the operator's verified identity
 * envelope (the SPA mints nothing). A plain anchor `href` would navigate the browser and could not
 * carry the request the way the typed reads do, and the JSON format is returned inline (no
 * attachment disposition), so we fetch the body ourselves — with `credentials: "same-origin"` so the
 * Cloudflare Access cookie is sent exactly as `apiFetch` does — then trigger a client-side download
 * of the returned blob. This keeps auth, tenancy and RBAC enforced server-side: a non-viewer gets a
 * `403` from the BFF and nothing downloads.
 *
 * The export does not go through `apiFetch` because that wrapper is JSON-only (it sets an
 * `application/json` Accept and parses the body as JSON); the CSV body is text and the JSON export is
 * a document we want saved to disk, not parsed. We still reuse `API_BASE` so the base path stays in
 * one place.
 *
 * Both formats are offered (Requirement 5.1). The mapping is advisory — this control only serializes
 * what the view already shows (Requirement 5.5); it adds no data and reads no framework logic
 * client-side.
 */

/** The two export formats the server accepts (the `format` query parameter). */
type ExportFormat = "csv" | "json";

const FORMAT_LABEL: Readonly<Record<ExportFormat, string>> = {
  csv: "CSV",
  json: "JSON",
};

export interface ExportControlProps {
  /** The framework currently selected in the view — the export is scoped to it. */
  readonly framework: ComplianceFramework;
  /** The window the view currently describes — the export covers exactly this range. */
  readonly window: ComplianceWindow;
}

export function ExportControl(props: ExportControlProps): ReactNode {
  // Which format (if any) is downloading, so the button can disable and announce progress; and the
  // last error, surfaced inline so a failed export is never silent.
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function download(format: ExportFormat): Promise<void> {
    setBusy(format);
    setError(null);
    try {
      await downloadExport(props.framework, props.window, format);
    } catch {
      setError(
        `The ${FORMAT_LABEL[format]} export could not be downloaded. The server enforces access; try again, and reload to sign in if your session has expired.`,
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="cmp-export">
      <span className="cmp-export__label" id="cmp-export-label">
        Export
      </span>
      <div className="cmp-export__buttons" role="group" aria-labelledby="cmp-export-label">
        {(["csv", "json"] as const).map((format) => (
          <button
            key={format}
            type="button"
            className="cmp-export__button"
            disabled={busy !== null}
            aria-busy={busy === format}
            onClick={() => void download(format)}
            aria-label={`Download the current ${props.framework} mapping for this window as ${FORMAT_LABEL[format]}`}
          >
            {busy === format ? `Preparing ${FORMAT_LABEL[format]}…` : FORMAT_LABEL[format]}
          </button>
        ))}
      </div>
      {error !== null && (
        <p className="cmp-export__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Fetch the export over the credentialed same-origin path and save it to disk. Resolves on a
 * successful download and throws on any non-2xx response or transport failure so the caller can
 * surface it.
 */
async function downloadExport(
  framework: ComplianceFramework,
  window: ComplianceWindow,
  format: ExportFormat,
): Promise<void> {
  const params = new URLSearchParams({
    framework,
    from: String(window.fromMs),
    to: String(window.toMs),
    format,
  });
  const response = await fetch(`${API_BASE}/compliance/export?${params.toString()}`, {
    method: "GET",
    // The Cloudflare Access cookie authenticates at this origin — send it exactly as `apiFetch` does
    // so the export rides the same identity the view already used.
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error(`export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const filename = exportFilename(response, framework, window, format);
  triggerDownload(blob, filename);
}

/**
 * The download filename. The CSV route sends a `content-disposition: attachment; filename="…"`; we
 * honor it when present so the server stays the single source of the naming convention, and
 * otherwise (the JSON route returns the document inline) build the same `compliance-<framework>-
 * <from>-<to>.<ext>` shape the server uses for CSV.
 */
function exportFilename(
  response: Response,
  framework: ComplianceFramework,
  window: ComplianceWindow,
  format: ExportFormat,
): string {
  const fromHeader = filenameFromDisposition(response.headers.get("content-disposition"));
  if (fromHeader !== null) return fromHeader;
  return `compliance-${framework}-${window.fromMs}-${window.toMs}.${format}`;
}

/** Pull the quoted `filename="…"` out of a `content-disposition` header, or null when absent. */
function filenameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match === null ? null : (match[1] ?? null);
}

/** Save a blob to disk under `filename` via a transient object URL and a synthetic anchor click. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
